'use client';

import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/rich-text';
import { Loader2, Save } from 'lucide-react';
import type { ProjectNote } from '@/types';

interface NoteEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { title: string; note: string; personal: boolean }) => Promise<void>;
    initialData?: ProjectNote;
    isSaving: boolean;
}

export const NoteEditorModal = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    isSaving,
}: NoteEditorModalProps) => {
    const [title, setTitle] = React.useState('');
    const [note, setNote] = React.useState('');
    const [personal, setPersonal] = React.useState(true);

    React.useEffect(() => {
        if (isOpen) {
            setTitle(initialData?.title || '');
            setNote(initialData?.note || '');
            setPersonal(initialData?.personal ?? true);
        }
    }, [isOpen, initialData]);

    const handleSave = async () => {
        if (!title.trim() || !note.trim()) return;
        await onSave({ title, note, personal });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0 overflow-hidden">
                <DialogHeader className="px-6 py-4 border-b">
                    <DialogTitle>{initialData ? 'Edit Note' : 'Add New Note'}</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div className="space-y-2">
                        <label className="text-sm font-semibold">Title</label>
                        <Input
                            placeholder="Enter note title..."
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="bg-background border-border/60 focus-visible:ring-primary shadow-sm"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-semibold">Note Content</label>
                        <div className="min-h-[300px] rounded-xl border border-border/60 bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 transition-all shadow-sm">
                            <RichTextEditor
                                value={note}
                                onChange={setNote}
                                placeholder="Start writing your note..."
                                className="min-h-[280px]"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                        <input
                            type="checkbox"
                            disabled={initialData && !initialData.personal} // If already shared, maybe keep it shared? User said share toggles personal.
                            id="personal-note"
                            checked={personal}
                            onChange={(e) => setPersonal(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <label htmlFor="personal-note" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Private Note (Only you can see this)
                        </label>
                    </div>
                </div>
                <DialogFooter className="px-6 py-4 border-t bg-muted/30">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving || !title.trim() || !note.trim()} className="gap-2">
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {initialData ? 'Update Note' : 'Create Note'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
